import { TILE_SOURCE_MEDIA_TYPE } from 'src/shared/catalogue/tileSource';

/**
 * The vocabulary an asset is described with, for the people filling in the form.
 *
 * What the engine *enforces* — the tile media type and the tile grid — lives in
 * `src/shared/catalogue/tileSource`, because the map reads those rules too. What is left here
 * is ours:
 *
 * - **Keys** are ours. STAC calls them "non-descriptive unique identifiers" and the engine
 *   reserves none. Standardising them is for the people reading the catalogue.
 * - **A projection on any geospatial asset** is ours too, and stricter than the engine, which
 *   demands one of a tile source alone. Coordinates mean nothing without the frame they are
 *   in, and a file that does not say leaves every consumer guessing.
 * - **Roles** are ours too, drawn from STAC's vocabulary. The engine reads none of them.
 */

/**
 * The projection offered for data assets — WGS84, what most producers compute in and what
 * GeoJSON mandates. Offered, never enforced: a dataset computed on a national grid is not
 * wrong, only different, and pinning one would refuse it for no reason we could name.
 */
export const DATA_CRS_CODE = 'EPSG:4326';

export type MediaTypeOption = {
    value: string;
    label: string;
    /** The engine will serve this asset as tiles, and so demands a `proj:code`. */
    isTileSource?: boolean;
    /** Holds coordinates, so we demand a `proj:code` even where the engine does not. */
    isGeospatial?: boolean;
};

/**
 * Every format this platform reads, or is about to.
 *
 * Deliberately short. A list padded with formats nothing consumes invites a producer to
 * catalogue something we cannot use, and the engine reads exactly one of these today — it
 * opens a PMTiles archive and does no more than check that every other object exists.
 *
 * A COG is here before anything can serve one: ADR-021 names it as the next source kind after
 * the archive, and cataloguing ahead of serving is how the tiles themselves arrived.
 *
 * Anything else is still publishable, through the form's free-text option.
 */
export const MEDIA_TYPE_OPTIONS: MediaTypeOption[] = [
    { value: TILE_SOURCE_MEDIA_TYPE, label: 'PMTiles archive — served as map tiles', isTileSource: true, isGeospatial: true },
    { value: 'application/vnd.apache.parquet', label: 'GeoParquet', isGeospatial: true },
    { value: 'application/vnd.flatgeobuf', label: 'FlatGeobuf', isGeospatial: true },
    {
        value: 'image/tiff; application=geotiff; profile=cloud-optimized',
        label: 'Cloud-Optimised GeoTIFF',
        isGeospatial: true,
    },
    { value: 'application/geo+json', label: 'GeoJSON', isGeospatial: true },
];

/** The type and subtype, as RFC 9110 compares them: case-insensitive, parameters ignored. */
const nameOf = (declared?: string): string => (declared ?? '').split(';')[0].trim().toLowerCase();

/**
 * Whether an asset holds coordinates, and so must say what frame they are in.
 *
 * Read from the list above rather than guessed, so a media type we do not recognise is asked
 * for nothing — demanding a projection of an unknown type would refuse assets the engine
 * accepts and we have no reason to reject.
 */
export const isGeospatialMediaType = (declared?: string): boolean => {
    const name = nameOf(declared);
    return MEDIA_TYPE_OPTIONS.some(option => option.isGeospatial && nameOf(option.value) === name);
};

export type AssetKeyOption = {
    value: string;
    label: string;
    /** What this key usually holds, so the two fields do not drift apart. */
    suggestedMediaType?: string;
};

export const ASSET_KEY_OPTIONS: AssetKeyOption[] = [
    { value: 'tiles', label: 'tiles — the archive the map draws from', suggestedMediaType: TILE_SOURCE_MEDIA_TYPE },
    { value: 'data', label: 'data — the version’s primary file', suggestedMediaType: 'application/vnd.apache.parquet' },
    { value: 'parquet', label: 'parquet — a GeoParquet rendering', suggestedMediaType: 'application/vnd.apache.parquet' },
    { value: 'flatgeobuf', label: 'flatgeobuf — a FlatGeobuf rendering', suggestedMediaType: 'application/vnd.flatgeobuf' },
    {
        value: 'cog',
        label: 'cog — a Cloud-Optimised GeoTIFF',
        suggestedMediaType: 'image/tiff; application=geotiff; profile=cloud-optimized',
    },
];

export type RoleOption = {
    value: string;
    description: string;
};

/**
 * What an asset is for. Two, not STAC's whole vocabulary: an asset here is either the data or
 * a rendering of it, and the engine reads neither — it picks a tile source by media type. A
 * role nothing publishes is a role nobody picks.
 */
export const ROLE_OPTIONS: RoleOption[] = [
    { value: 'data', description: 'The actual data, rather than a picture of it.' },
    { value: 'visual', description: 'Made to be looked at — a rendering for display.' },
];
